import { Dispatch, SetStateAction, useState } from "react";
import { Highlighter } from "react-bootstrap-typeahead";
import { get, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as AddressBookIcon } from "assets/images/icons/AddressBook.svg";
import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { Address } from "api/models";
import { getCompanyAddressV2Details } from "api";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import Button from "components/atoms/Button/Button";
import AddressV2Dialog from "components/organisms/AddressV2Dialog/AddressV2Dialog";
import { CreateUpdateCompanyAddressV2ResponseType } from "api/response";
import testIds from "constants/testIds";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import SearchSelect from "../../Common/SearchSelect";
import { IOptionProps } from "../Types";
import { AddressOption, formatAddress } from "./helpers";

interface AddressFormProps {
  isPickupOrDelivery: string;
  formState: RequestFormState;
  addressBook: Address[];
  setSearchPlace: Function;
  setEditAddress: Dispatch<SetStateAction<boolean>>;
  autoFillAddress: (event: any) => void;
  getAddresses: () => void;
}

export default function AddressForm({
  isPickupOrDelivery,
  addressBook,
  formState,
  setSearchPlace,
  setEditAddress,
  autoFillAddress,
  getAddresses,
}: AddressFormProps) {
  const intl = useIntl();
  const { permissions }: any = usePermissions();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const addressBookPermissions = permissions ? JSON.parse(permissions).permissions.quoting : null;
  const { enable_new_address_structure } = JSON.parse(permissions)?.company_shipper_settings;
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [isFetchingSelectedAddress, setIsFetchingSelectedAddress] = useState(false);
  const selectedAddressName = useWatch({
    name: `schedule.${isPickupOrDelivery}_address_name`,
  });

  const {
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();
  const scheduleAddressType = useWatch({
    control,
    name: `schedule.${isPickupOrDelivery}_address_type`,
  });
  const isOptionalAirportOrSeaport =
    isOptionalPortAddressFormEnabled &&
    enable_new_address_structure &&
    (scheduleAddressType === "airport" || scheduleAddressType === "seaport");

  const handleAddNewAddress = (data: CreateUpdateCompanyAddressV2ResponseType) => {
    if (!data) return;
    autoFillAddress([data]);
  };

  const handleManualAddress = () => {
    if (
      getValues(`schedule.${isPickupOrDelivery}_address_name`) &&
      !getValues(`schedule.${isPickupOrDelivery}_country`) &&
      !getValues(`schedule.${isPickupOrDelivery}_city`)
    ) {
      setEditAddress(true);
      setValue(`schedule.${isPickupOrDelivery}_country`, "", { shouldValidate: true });
    }
  };

  const checkAddressMatch = () => {
    if (
      !getValues("schedule.pickup_country") &&
      !getValues("schedule.pickup_street") &&
      !getValues("schedule.pickup_zipcode") &&
      !getValues("schedule.stop1_country") &&
      !getValues("schedule.stop1_street") &&
      !getValues("schedule.stop1_zipcode")
    ) {
      return true;
    }
    if (
      getValues("schedule.pickup_country") === getValues("schedule.stop1_country") &&
      getValues("schedule.pickup_street") === getValues("schedule.stop1_street") &&
      getValues("schedule.pickup_zipcode") === getValues("schedule.stop1_zipcode")
    ) {
      return false;
    }
    return true;
  };

  const handleEditAddress = async () => {
    const codeType = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
    const currentAddressReference = getValues(`schedule.${codeType}_address_reference_number`);
    if (!currentAddressReference) return;

    let currentAddress: any = addressBook.find((address) => address.reference_number === currentAddressReference);
    if (!currentAddress) {
      try {
        setIsFetchingSelectedAddress(true);
        currentAddress = (await getCompanyAddressV2Details(currentAddressReference)).data;
        ["city", "country", "state", "locode"].forEach((item) => {
          currentAddress[`${item}_id`] = currentAddress[item];
        });
      } catch (error) {
        return;
      } finally {
        setIsFetchingSelectedAddress(false);
      }
    }
    if (currentAddress) {
      setSelectedAddress(currentAddress);
      setShowAddressDialog(true);
    }
  };

  const handleRenderOption = ({ option, text }: { option: IOptionProps; text: string }) => {
    if (enable_new_address_structure) return <AddressOption option={option} />;

    return (
      <div>
        <div className="w-100 d-flex justify-content-between">
          <div className="font-weight-bold font-12">
            <Highlighter search={text}>{option?.label}</Highlighter>
          </div>
          <div>
            <AddressBookIcon />
          </div>
        </div>
        <div className="font-b3 font-weight-normal mt-1 text-secondary">
          <Highlighter search={text}>{option?.street_address}</Highlighter>
        </div>
        <div className="font-b3 mt-1 text-secondary">
          {option?.zipcode && (
            <Highlighter search={text}>{text.includes(option?.zipcode) ? option?.zipcode : option?.zipcode}</Highlighter>
          )}
          ,{option?.city_name && <Highlighter search={text}>{option?.city_name}</Highlighter>},
          {option?.country_name && <Highlighter search={text}>{option?.country_name}</Highlighter>}
        </div>
      </div>
    );
  };

  return (
    <>
      <SearchSelect
        name={`schedule.${isPickupOrDelivery}_address_name`}
        testId={
          isPickupOrDelivery === "pickup"
            ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_ADDRESS_SELECT
            : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_ADDRESS_SELECT
        }
        preselect={false}
        enableManualValue={!enable_new_address_structure}
        required={!isOptionalAirportOrSeaport}
        disableAutoClear={enable_new_address_structure}
        options={addressBook?.map((address) => formatAddress(address))}
        label={
          enable_new_address_structure
            ? intl.formatMessage({
                id: "new-request.schedule.address",
                defaultMessage: "Address",
              })
            : ""
        }
        placeholder={
          enable_new_address_structure
            ? intl.formatMessage({
                id: "new-request.schedule.chooseAddressV2Placeholder",
                defaultMessage: "Search by address, location reference number, or custom reference",
              })
            : intl.formatMessage({
                id: "new-request.schedule.chooseCompany",
                defaultMessage: "Search for an address..",
              })
        }
        // disable client side filtering if new address structure is enabled
        filterBy={
          enable_new_address_structure ? () => true : ["label", "street_address", "zipcode", "city_name", "country_name"]
        }
        handleRenderOption={handleRenderOption}
        onInputChange={(val: string) => {
          setSearchPlace(val);
          if (!val?.trim()) {
            const codeType = isPickupOrDelivery === "stop1" ? "destination" : isPickupOrDelivery;
            setValue(`schedule.${codeType}_address_reference_number`, "");
            setValue(`schedule.${codeType}_address_pk`, undefined);
          }
        }}
        disable={formState.disabledFields[`schedule.${isPickupOrDelivery}_address_name`]}
        onSelect={autoFillAddress}
        handleOnBlur={handleManualAddress}
        {...(isPickupOrDelivery !== "pickup" && {
          validations: {
            validate: {
              isSameAddress: () =>
                checkAddressMatch() ||
                intl.formatMessage({
                  id: "new-request.schedule.deliveryAddressCannotSameOriginAddress",
                  defaultMessage: "Delivery address cannot be the same as origin address",
                }),
            },
          },
        })}
        extraInfo={
          addressBookPermissions?.restricted_to_address_book &&
          !get(errors, `schedule.${isPickupOrDelivery}_address_name`) &&
          getValues(`schedule.${isPickupOrDelivery}_address_name`) &&
          !addressBook.find((address) => address.name === getValues(`schedule.${isPickupOrDelivery}_address_name`)) && (
            <div className="d-block font-b3 invalid-feedback">
              <FormattedMessage
                id="general.companyNameError"
                defaultMessage="You are only able to ship to companies that are available in the address book."
              />
            </div>
          )
        }
      />
      {enable_new_address_structure && (
        <div className="d-flex justify-content-between align-items-start mt-xs">
          {selectedAddressName && (
            <div className="d-flex">
              <div className="font-b3">
                <div className="font-weight-bold">{getValues(`schedule.${isPickupOrDelivery}_address_name`)}</div>
                <div>{getValues(`schedule.${isPickupOrDelivery}_street`)}</div>
                <div>{getValues(`schedule.${isPickupOrDelivery}_street_line2`)}</div>
                <div>
                  {getValues(`schedule.${isPickupOrDelivery}_city`)}
                  {getValues(`schedule.${isPickupOrDelivery}_state`) &&
                    `, ${getValues(`schedule.${isPickupOrDelivery}_state`)}`}
                  {getValues(`schedule.${isPickupOrDelivery}_country`) &&
                    `, ${getValues(`schedule.${isPickupOrDelivery}_country`)}`}{" "}
                  {getValues(`schedule.${isPickupOrDelivery}_zipcode`)}
                </div>
                <AddressLocodeAndCustomFields
                  un_locode={getValues(`schedule.${isPickupOrDelivery}_un_locode`)}
                  location_reference_number={getValues(`schedule.${isPickupOrDelivery}_location_reference_number`)}
                  custom_address_reference={getValues(`schedule.${isPickupOrDelivery}_custom_address_reference`)}
                  un_locode_className="font-b4"
                />
              </div>
              {addressBookPermissions?.allow_address_book_entry && (
                <div className="align-self-end ml-md cursor-pointer">
                  {isFetchingSelectedAddress ? (
                    <div className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <EditPencil onClick={handleEditAddress} />
                  )}
                </div>
              )}
            </div>
          )}
          {addressBookPermissions?.allow_address_book_entry && (
            <div className="d-flex">
              <Button variant="link" className="p-xs" onClick={() => setShowAddressDialog(true)}>
                <span className="font-b4">
                  + <FormattedMessage id="new-request.schedule.addNewAddress" defaultMessage="Add new address" />
                </span>
              </Button>
              <AddressV2Dialog
                onClose={() => {
                  setShowAddressDialog(false);
                  setSelectedAddress(null);
                }}
                isOpen={showAddressDialog}
                refetchAddresses={getAddresses}
                source="quick_quote_request"
                onAddNewAddress={handleAddNewAddress}
                selectedAddress={selectedAddress}
                forceCreate
                formSelectedShipperLocation={getValues("organizational.location")}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
